import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, ListIcon, PlusCircleIcon, SearchIcon, SyncAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminSplaConsumption } from '../../../hooks/useAdminSplaConsumption';
import { formatUnitPrice } from '../../../utils/money';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import { SplaSubjectCell } from '../../utils/splaSubjects';
import type { SplaConsumptionRow, SplaConsumptionSource } from '../../../types/models/Spla';
import type { ApiResponseSplaConsumptionDeleted } from '../../../types/api/ApiResponseSpla';
import ClientTypeahead, { type ClientOption } from '../ProductsAndPrices/ClientTypeahead';
import SplaConsumptionModal from './SplaConsumptionModal';

const SOURCE_LABELS: Record<SplaConsumptionSource, { label: string; color: 'blue' | 'purple' | 'teal' }> = {
  user: { label: 'user', color: 'blue' },
  agent: { label: 'agent', color: 'purple' },
  computed: { label: 'computed', color: 'teal' },
};

/** Compact source badge with the provenance explained on hover. */
function SourceBadge({ source }: { source: SplaConsumptionSource }) {
  const descriptions: Record<SplaConsumptionSource, string> = {
    user: 'Maintained by staff through this screen.',
    agent: 'Reported by the manager-agent module.',
    computed: 'Machine-maintained by a binding — durable changes belong on the Bindings tab.',
  };
  const { label, color } = SOURCE_LABELS[source];
  return (
    <Tooltip content={descriptions[source]}>
      <Label color={color} isCompact>
        {label}
      </Label>
    </Tooltip>
  );
}

interface DeleteModalProps {
  row: SplaConsumptionRow;
  onClose: () => void;
  /** Performs the DELETE; resolves `true` on success (the parent owns the toasts). */
  onDelete: (row: SplaConsumptionRow) => Promise<boolean>;
}

// Hard delete of one CURRENT-state row — frozen monthly snapshots are their own records and are
// deliberately untouched, which is exactly what the confirm copy says.
function SplaConsumptionDeleteModal({ row, onClose, onDelete }: DeleteModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    const ok = await onDelete(row);
    setIsDeleting(false);
    if (ok) {
      onClose();
    }
  };

  return (
    <Modal variant="small" isOpen onClose={onClose} aria-label="Delete consumption row">
      <ModalHeader title="Delete consumption row" titleIconVariant="warning" />
      <ModalBody>
        <Content component="p">
          Delete the consumption of <strong>{row.license.part_number ?? `license #${row.license.id}`}</strong> by{' '}
          <strong>{row.subject.name}</strong> for {row.client.name ?? `client #${row.client.id}`}?
        </Content>
        <Content component="p">
          This removes the current state only — frozen monthly snapshots are unaffected. The license drops
          off the next report unless it is re-added (or a binding re-computes it).
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleConfirm()} isLoading={isDeleting} isDisabled={isDeleting}>
          Delete
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isDeleting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface SplaConsumptionTabProps {
  /** Defers every admin fetch until the page's staff check has passed. */
  isStaff: boolean;
  /** Bumped by the Bindings/Candidates tabs when computed rows changed — triggers a refetch. */
  refreshKey: number;
}

/**
 * The CURRENT per-subject consumption state — one row per (client × license × subject), the working
 * set every monthly snapshot reads. Mutable and hard-deletable; the audit trail is the frozen
 * monthly snapshot on the Usage tab, never this table. Rows with `source: computed` belong to a
 * binding — the edit modal warns that the next recompute reclaims them.
 */
function SplaConsumptionTab({ isStaff, refreshKey }: SplaConsumptionTabProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [clientFilter, setClientFilter] = useState<number | null>(null);
  const { consumption, loading, error, refetch, applyRow, removeRow } = useAdminSplaConsumption(
    isStaff,
    clientFilter,
    refreshKey,
  );

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search so filtering doesn't run on every keystroke (CLAUDE.md: 400ms).
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchValue), 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  // Client filter options are ACCUMULATED from the rows rather than fetched from /clients (which
  // fails during an impersonation session): filtering server-side would otherwise shrink the option
  // list to the single selected client, leaving no way back.
  const [clientNames, setClientNames] = useState<Map<number, string>>(new Map());
  useEffect(() => {
    setClientNames((prev) => {
      const next = new Map(prev);
      let changed = false;
      consumption.forEach((row) => {
        const name = row.client.name ?? `Client #${row.client.id}`;
        if (next.get(row.client.id) !== name) {
          next.set(row.client.id, name);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [consumption]);

  const clientOptions = useMemo<ClientOption[]>(
    () =>
      Array.from(clientNames.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [clientNames],
  );

  // Client-side text search across the human-friendly fields: client, license (part number, item
  // name, product family), and subject name.
  const filteredRows = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) {
      return consumption;
    }
    return consumption.filter((row) =>
      [
        row.client.name ?? '',
        row.license.part_number ?? '',
        row.license.item_name ?? '',
        row.license.product_family ?? '',
        row.subject.name,
      ]
        .join('\n')
        .toLowerCase()
        .includes(q),
    );
  }, [consumption, debouncedSearch]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<SplaConsumptionRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<SplaConsumptionRow | null>(null);

  const handleSaved = useCallback(
    (row: SplaConsumptionRow, isCreate: boolean) => {
      if (isCreate) {
        void refetch();
      } else {
        applyRow(row);
      }
    },
    [refetch, applyRow],
  );

  const handleDelete = useCallback(
    async (row: SplaConsumptionRow): Promise<boolean> => {
      try {
        const response = await apiFetch(`/admin/spla/consumption/${row.id}`, { method: 'DELETE' });
        // Branch on the JSend body.status, NEVER response.ok.
        const body = (await response.json().catch(() => null)) as ApiResponseSplaConsumptionDeleted | null;
        if (body?.status === 'success' && body.data?.deleted) {
          removeRow(row.id);
          addNotification({
            title: 'Consumption row deleted',
            srTitle: 'Success alert',
            description: `${row.subject.name} no longer consumes ${row.license.part_number ?? `license #${row.license.id}`}. Frozen monthly snapshots are unaffected.`,
            variant: 'success',
          });
          setDrawerOpen(true);
          return true;
        }
        addNotification({
          title: 'Could not delete the consumption row',
          srTitle: 'Error alert',
          description: body?.message ?? 'Could not delete the consumption row. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        return false;
      } catch (err) {
        addNotification({
          title: 'Could not delete the consumption row',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        return false;
      }
    },
    [apiFetch, removeRow, addNotification, setDrawerOpen],
  );

  const rowActions = (row: SplaConsumptionRow): IAction[] => [
    {
      title: 'Edit',
      onClick: () => setEditRow(row),
    },
    {
      title: 'Delete',
      isDanger: true,
      onClick: () => setDeleteRow(row),
    },
  ];

  const clearAllFilters = () => {
    setSearchValue('');
    setClientFilter(null);
  };

  const hasActiveFilter = debouncedSearch.trim() !== '' || clientFilter !== null;

  const renderLicenseCell = (row: SplaConsumptionRow) => (
    <>
      {row.license.category_name !== null && <Content component="small">{row.license.category_name} · </Content>}
      {row.license.part_number ?? `#${row.license.id}`}
      <div>
        <Content component="small">{row.license.product_family ?? ''}</Content>
      </div>
      {row.license.archived === true && (
        <Label color="grey" isCompact>
          Retired
        </Label>
      )}
    </>
  );

  const renderPriceCell = (row: SplaConsumptionRow) => {
    if (row.sale_unit_price === null) {
      return (
        <Tooltip content="No sale price resolves for this client — the customer page shows it unpriced and invoicing will fail loud.">
          <Label color="orange" isCompact>
            Unpriced
          </Label>
        </Tooltip>
      );
    }
    return (
      <>
        {formatUnitPrice(row.sale_unit_price)}
        {row.sale_is_override === true && (
          <>
            {' '}
            <Tooltip content="A per-client override supplies this price (not the platform default).">
              <Label color="yellow" isCompact>
                override
              </Label>
            </Tooltip>
          </>
        )}
      </>
    );
  };

  const renderNoteCell = (note: string | null) =>
    note !== null && note !== '' ? (
      <Tooltip content={note}>
        <span
          style={{
            display: 'inline-block',
            maxWidth: '10rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'bottom',
          }}
        >
          {note}
        </span>
      </Tooltip>
    ) : (
      ''
    );

  const renderContent = () => {
    if (loading && consumption.length === 0) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load the consumption"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (consumption.length === 0 && !hasActiveFilter) {
      return (
        <EmptyState titleText="No consumption recorded" icon={ListIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            No subject currently consumes an SPLA license. Add a row by hand, or let bindings compute them.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Add consumption
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (filteredRows.length === 0) {
      return (
        <EmptyState titleText="No rows match your filters" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Try a different search term or client, or clear the filters.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={clearAllFilters}>
                Clear filters
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <Table aria-label="SPLA consumption rows" variant="compact">
        <Thead>
          <Tr>
            <Th>Client</Th>
            <Th>License</Th>
            <Th>Subject</Th>
            <Th>Quantity</Th>
            <Th>Sale price</Th>
            <Th>Source</Th>
            <Th>Note</Th>
            <Th>Updated</Th>
            <Th screenReaderText="Row actions" />
          </Tr>
        </Thead>
        <Tbody>
          {filteredRows.map((row) => (
            <Tr key={row.id}>
              <Td dataLabel="Client">{row.client.name ?? `Client #${row.client.id}`}</Td>
              <Td dataLabel="License">{renderLicenseCell(row)}</Td>
              <Td dataLabel="Subject">
                <SplaSubjectCell type={row.subject.type} subjectRef={row.subject.ref} name={row.subject.name} />
              </Td>
              <Td dataLabel="Quantity">{row.quantity}</Td>
              <Td dataLabel="Sale price">{renderPriceCell(row)}</Td>
              <Td dataLabel="Source">
                <SourceBadge source={row.source} />
              </Td>
              <Td dataLabel="Note">{renderNoteCell(row.note)}</Td>
              <Td dataLabel="Updated">{formatWallClockDateTime(row.updated_at)}</Td>
              <Td isActionCell>
                <ActionsColumn items={rowActions(row)} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <>
      <Toolbar clearAllFilters={clearAllFilters}>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by client, license or subject..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          <ToolbarItem style={{ minWidth: '16rem' }}>
            <ClientTypeahead
              clients={clientOptions}
              value={clientFilter}
              onChange={setClientFilter}
              idPrefix="spla-consumption-filter-client"
            />
          </ToolbarItem>
          <ToolbarGroup align={{ default: 'alignEnd' }}>
            <ToolbarItem>
              <Button
                variant="primary"
                icon={<PlusCircleIcon />}
                onClick={() => setIsCreateOpen(true)}
                isDisabled={loading && consumption.length === 0}
              >
                Add consumption
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button
                variant="secondary"
                icon={<SyncAltIcon />}
                onClick={() => void refetch()}
                isLoading={loading}
                isDisabled={loading}
              >
                Refresh
              </Button>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
      {renderContent()}
      {isCreateOpen && (
        <SplaConsumptionModal isOpen row={null} onClose={() => setIsCreateOpen(false)} onSaved={handleSaved} />
      )}
      {editRow && (
        <SplaConsumptionModal
          key={editRow.id}
          isOpen
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={handleSaved}
        />
      )}
      {deleteRow && (
        <SplaConsumptionDeleteModal row={deleteRow} onClose={() => setDeleteRow(null)} onDelete={handleDelete} />
      )}
    </>
  );
}

export default SplaConsumptionTab;
